import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { eventService } from '@/services/eventService';
import { mesaService } from '@/services/mesaService';
import { Event, Guest, Mesa } from '@/types';

const PRINT_CSS = `
  @media print {
    @page { size: letter; margin: 12mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
    .mesa-block { break-inside: avoid; page-break-inside: avoid; }
  }
`;

const formatDate = (date: Date) =>
  date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const EventSeatingPrint: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    let active = true;
    (async () => {
      try {
        const [ev, ms, gs] = await Promise.all([
          eventService.getEvent(eventId),
          mesaService.getEventMesas(eventId),
          eventService.getEventGuests(eventId),
        ]);
        if (!active) return;
        setEvent(ev);
        setMesas(ms);
        setGuests(gs);
      } catch (err) {
        console.error('Error loading print data:', err);
        if (active) setError(err instanceof Error ? err.message : 'Error cargando datos');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventId]);

  // Trigger print dialog automatically once everything has rendered.
  useEffect(() => {
    if (!loading && !error && event && mesas.length >= 0) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
  }, [loading, error, event, mesas]);

  const guestsByMesa = useMemo(() => {
    const map = new Map<string, Guest[]>();
    for (const g of guests) {
      const key = g.mesa_id ?? '__unassigned__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(g);
    }
    for (const list of map.values()) list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es-MX'));
    return map;
  }, [guests]);

  const unassigned = guestsByMesa.get('__unassigned__') ?? [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="p-8">
        <p className="text-red-600">{error ?? 'Evento no encontrado'}</p>
      </div>
    );
  }

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="no-print mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Imprimir
          </button>
        </div>
        <header className="mb-6 border-b pb-3">
          <h1 className="text-2xl font-bold">{event.nombre}</h1>
          <p className="text-sm text-gray-600">
            {formatDate(event.fecha)} · Lista de mesas
          </p>
        </header>

        {mesas.length === 0 ? (
          <p className="text-gray-500 italic">No hay mesas configuradas.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {mesas.map((mesa) => {
              const occupants = guestsByMesa.get(mesa.id) ?? [];
              return (
                <section key={mesa.id} className="mesa-block border rounded-md p-3">
                  <h2 className="text-lg font-semibold mb-1">
                    Mesa {mesa.numero}
                    {mesa.nombre ? ` — ${mesa.nombre}` : ''}
                  </h2>
                  <p className="text-xs text-gray-500 mb-2">
                    Capacidad {mesa.capacidad} · {mesa.forma.toLowerCase()}
                  </p>
                  {occupants.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Sin invitados asignados.</p>
                  ) : (
                    <ol className="list-decimal pl-5 space-y-0.5 text-sm">
                      {occupants.map((g) => (
                        <li key={g.id}>
                          {g.nombre}
                          {g.numero_acompanantes > 0 && (
                            <span className="text-gray-600"> (+{g.numero_acompanantes})</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {unassigned.length > 0 && (
          <section className="mt-6 border-t pt-3 mesa-block">
            <h2 className="text-lg font-semibold mb-2">Invitados sin mesa asignada</h2>
            <ol className="list-decimal pl-5 space-y-0.5 text-sm grid grid-cols-1 sm:grid-cols-2">
              {unassigned.map((g) => (
                <li key={g.id}>
                  {g.nombre}
                  {g.numero_acompanantes > 0 && (
                    <span className="text-gray-600"> (+{g.numero_acompanantes})</span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </>
  );
};
